'use client';

import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
} from 'react';
import { useTranslations } from 'next-intl';
import { useModal } from 'components/shared/Modal';
import { ButtonV2 } from 'components/shared/ButtonV2';
import { ImageCardModal } from 'components/shared/ImageCardModal';
import { TypeformSnippet } from 'components/shared/TypeformSnippet';
import { CustomImage } from 'components/shared/custom-image';
import { useIdlePopup } from 'hooks/popup/useIdlePopup';
import {
  DEFAULT_PRODUCT,
  DEFAULT_TYPEFORM_ID,
  RING_AIR_POPUP_IMAGE,
  RING_PRO_POPUP_IMAGE,
  RING_PRO_POPUP_SAMPLES_IMAGE,
  SIZING_KIT_POPUP_TRACKING,
  storageKeysForProduct,
  type ClaimSizingKitPopupHandle,
  type ClaimSizingKitPopupProps,
} from './constants';
import styles from './index.module.css';

export type { ClaimSizingKitPopupHandle } from './constants';

const RingProKitImage = ({ alt }: { alt: string }) => (
  <div className={styles.kitPair} role="img" aria-label={alt}>
    <div className={styles.kitSamplesFrame}>
      <CustomImage
        className={styles.kitSamples}
        src={RING_PRO_POPUP_SAMPLES_IMAGE}
        alt=""
        width={1080}
        height={1080}
      />
    </div>
    <div className={styles.kitBoxFrame}>
      <CustomImage
        className={styles.kitBox}
        src={RING_PRO_POPUP_IMAGE}
        alt=""
        width={800}
        height={800}
      />
    </div>
  </div>
);

export const ClaimSizingKitPopup = forwardRef<
  ClaimSizingKitPopupHandle,
  ClaimSizingKitPopupProps
>(
  (
    {
      delayMs,
      inactivityMs,
      typeformId = DEFAULT_TYPEFORM_ID,
      region,
      product = DEFAULT_PRODUCT,
    },
    ref,
  ) => {
    const t = useTranslations('common');
    const ModalClient = useModal();
    const storageKeys = useMemo(
      () => storageKeysForProduct(product),
      [product],
    );
    const trackingData = useMemo(
      () => ({ region, product, variant: 'sizing_kit' }),
      [region, product],
    );
    const { isVisible, cancel, dismiss, complete } = useIdlePopup({
      storageKeys,
      tracking: SIZING_KIT_POPUP_TRACKING,
      delayMs,
      inactivityMs,
      trackingData,
    });

    useImperativeHandle(ref, () => ({ cancelPopup: cancel }), [cancel]);

    const handleAccept = useCallback(() => {
      complete();
      const regionMedium = region?.toUpperCase();
      const trackingParams = {
        utm_source: 'websk',
        utm_content: product,
        product,
        ...(regionMedium ? { utm_medium: regionMedium } : {}),
      };
      ModalClient.set(
        <div className={styles.typeformModal}>
          <TypeformSnippet
            typeformId={typeformId}
            frameTitle={t(
              'claimSizingKitPopup.typeformSnippet.frameTitle.claimYourFree',
            )}
            className={styles.typeformContainer}
            trackingParams={trackingParams}
          />
        </div>,
      );
      ModalClient.setCloseButtonTheme('light');
      ModalClient.show();
    }, [typeformId, region, product, complete, ModalClient, t]);

    if (!isVisible) return null;

    return (
      <ImageCardModal
        onClose={dismiss}
        image={
          product === 'ring_pro' ? (
            <RingProKitImage
              alt={t('claimSizingKitPopup.kitImage.alt.ultrahumanRingPro')}
            />
          ) : (
            <CustomImage
              className={styles.kitImageAir}
              src={RING_AIR_POPUP_IMAGE}
              alt={t('claimSizingKitPopup.kitImage.alt.ultrahumanRingAir')}
              width={1200}
              height={675}
            />
          )
        }
        title={t('claimSizingKitPopup.title.claimYourFree')}
        subtitle={t('claimSizingKitPopup.subtitle.sureAboutYour')}
        actions={
          <ButtonV2 variant="blue" onClick={handleAccept}>
            {t('claimSizingKitPopup.cTALink.wantFreeSizing')}
          </ButtonV2>
        }
      />
    );
  },
);
ClaimSizingKitPopup.displayName = 'ClaimSizingKitPopup';
